import type { GatewayBrowserClient } from "../gateway.ts";
import type { ModelCatalogEntry } from "../types.ts";
import { cloneConfigObject, setPathValue, serializeConfigForm } from "./config/form-utils.ts";

export type ModelDef = {
  id: string;
  name: string;
  reasoning?: boolean;
  contextWindow?: number;
};

export type ProviderDef = {
  id: string;
  name: string;
  group: "cn" | "intl" | "local";
  envVar: string;
  defaultBaseUrl: string;
  variants?: Array<{ label: string; baseUrl: string }>;
  defaultModels: ModelDef[];
};

export const PROVIDER_REGISTRY: ProviderDef[] = [
  {
    id: "deepseek",
    name: "DeepSeek",
    group: "cn",
    envVar: "DEEPSEEK_API_KEY",
    defaultBaseUrl: "https://api.deepseek.com/v1",
    defaultModels: [
      { id: "deepseek-chat", name: "DeepSeek V3", contextWindow: 65536 },
      { id: "deepseek-reasoner", name: "DeepSeek R1", reasoning: true, contextWindow: 65536 },
    ],
  },
  {
    id: "zai",
    name: "智谱 GLM",
    group: "cn",
    envVar: "ZAI_API_KEY",
    defaultBaseUrl: "https://open.bigmodel.cn/api/paas/v4",
    variants: [
      { label: "国内", baseUrl: "https://open.bigmodel.cn/api/paas/v4" },
      { label: "国际", baseUrl: "https://api.z.ai/v1" },
    ],
    defaultModels: [
      { id: "glm-4-plus", name: "GLM-4 Plus", contextWindow: 128000 },
      { id: "glm-4-flash", name: "GLM-4 Flash", contextWindow: 128000 },
      { id: "glm-4-long", name: "GLM-4 Long", contextWindow: 1000000 },
    ],
  },
  {
    id: "moonshot",
    name: "月之暗面 Kimi",
    group: "cn",
    envVar: "MOONSHOT_API_KEY",
    defaultBaseUrl: "https://api.moonshot.cn/v1",
    defaultModels: [
      { id: "kimi-k2-0429", name: "Kimi K2", contextWindow: 131072 },
      { id: "moonshot-v1-128k", name: "Moonshot V1 128K", contextWindow: 131072 },
      { id: "moonshot-v1-32k", name: "Moonshot V1 32K", contextWindow: 32768 },
    ],
  },
  {
    id: "modelstudio",
    name: "通义千问",
    group: "cn",
    envVar: "MODELSTUDIO_API_KEY",
    defaultBaseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1",
    variants: [
      { label: "国内", baseUrl: "https://dashscope.aliyuncs.com/compatible-mode/v1" },
      { label: "国际", baseUrl: "https://dashscope-intl.aliyuncs.com/compatible-mode/v1" },
    ],
    defaultModels: [
      { id: "qwen-plus", name: "Qwen Plus", contextWindow: 131072 },
      { id: "qwen-turbo", name: "Qwen Turbo", contextWindow: 131072 },
      { id: "qwen-max", name: "Qwen Max", contextWindow: 32768 },
      { id: "qwen-long", name: "Qwen Long", contextWindow: 10000000 },
    ],
  },
  {
    id: "volcengine",
    name: "火山引擎",
    group: "cn",
    envVar: "VOLCANO_ENGINE_API_KEY",
    defaultBaseUrl: "https://ark.cn-beijing.volces.com/api/v3",
    defaultModels: [
      { id: "doubao-pro-32k", name: "豆包 Pro 32K", contextWindow: 32768 },
      { id: "doubao-lite-32k", name: "豆包 Lite 32K", contextWindow: 32768 },
    ],
  },
  {
    id: "qianfan",
    name: "百度千帆",
    group: "cn",
    envVar: "QIANFAN_API_KEY",
    defaultBaseUrl: "https://qianfan.baidubce.com/v2",
    defaultModels: [
      { id: "ernie-4.0-turbo-128k", name: "文心一言 4.0 Turbo 128K", contextWindow: 131072 },
      { id: "ernie-speed-128k", name: "ERNIE Speed 128K", contextWindow: 131072 },
      { id: "ernie-lite-128k", name: "ERNIE Lite 128K", contextWindow: 131072 },
    ],
  },
  {
    id: "minimax",
    name: "MiniMax",
    group: "cn",
    envVar: "MINIMAX_API_KEY",
    defaultBaseUrl: "https://api.minimax.chat/v1",
    defaultModels: [
      { id: "abab6.5s-chat", name: "ABAB 6.5s", contextWindow: 245760 },
      { id: "abab5.5-chat", name: "ABAB 5.5", contextWindow: 16384 },
    ],
  },
  {
    id: "xiaomi",
    name: "小米",
    group: "cn",
    envVar: "XIAOMI_API_KEY",
    defaultBaseUrl: "",
    defaultModels: [
      { id: "MiMo-7B-RL", name: "MiMo 7B", contextWindow: 32768 },
    ],
  },
];

export type ProviderStatus = {
  configured: boolean;
  modelCount: number;
};

export function resolveProviderStatuses(
  config: Record<string, unknown> | null,
  models: ModelCatalogEntry[],
): Record<string, ProviderStatus> {
  const result: Record<string, ProviderStatus> = {};
  const configObj = config && typeof config === "object" ? config : null;
  const modelsConfig = configObj?.models as Record<string, unknown> | undefined;
  const providerEntries =
    modelsConfig && typeof modelsConfig === "object"
      ? (modelsConfig.providers as Record<string, unknown> | undefined)
      : undefined;

  for (const def of PROVIDER_REGISTRY) {
    const entry = providerEntries?.[def.id] as Record<string, unknown> | undefined;
    const hasKey = Boolean(entry?.apiKey);
    const modelCount = models.filter((m) => m.provider === def.id).length;
    result[def.id] = { configured: hasKey, modelCount };
  }
  return result;
}

/**
 * Parse extra model IDs from a comma/newline/space separated string
 * into ModelDef entries.
 */
export function parseExtraModels(input: string): ModelDef[] {
  if (!input.trim()) {
    return [];
  }
  return input
    .split(/[,\n]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map((id) => ({ id, name: id }));
}

/**
 * Save provider config using config.set (no gateway restart).
 *
 * Uses the same approach as the built-in config page: mutate the configForm
 * object at specific paths using setPathValue, then serialize and submit
 * the full form. This ensures only the touched fields are overwritten, and
 * other providers' redacted API keys (__OPENCLAW_REDACTED__) are left intact
 * since config.set on the server side preserves untouched fields.
 *
 * Returns the updated configForm for the caller to store back into state.
 */
export async function saveProviderConfig(
  client: GatewayBrowserClient,
  providerId: string,
  apiKey: string,
  baseUrl: string,
  defaultModels: ModelDef[],
  extraModelsInput: string,
  setAsDefault: boolean,
  defaultModelId: string,
  configForm: Record<string, unknown>,
  baseHash: string,
): Promise<Record<string, unknown>> {
  const form = cloneConfigObject(configForm);

  // Set provider API key
  if (apiKey.trim()) {
    setPathValue(form, ["models", "providers", providerId, "apiKey"], apiKey.trim());
  }
  // Set provider base URL
  if (baseUrl.trim()) {
    setPathValue(form, ["models", "providers", providerId, "baseUrl"], baseUrl.trim());
  }

  // Merge default models + extra models
  const extraModels = parseExtraModels(extraModelsInput);
  const allModels = [...defaultModels, ...extraModels];
  const modelEntries = allModels.map((m) => {
    const entry: Record<string, unknown> = { id: m.id, name: m.name };
    if (m.reasoning) {
      entry.reasoning = true;
    }
    if (m.contextWindow) {
      entry.contextWindow = m.contextWindow;
    }
    return entry;
  });
  setPathValue(form, ["models", "providers", providerId, "models"], modelEntries);

  // Set as default model (format: "provider/model-id")
  if (setAsDefault && defaultModelId) {
    setPathValue(form, ["agents", "defaults", "model"], `${providerId}/${defaultModelId}`);
  }

  await client.request("config.set", {
    raw: serializeConfigForm(form),
    baseHash,
  });

  return form;
}

/**
 * Test provider connection by directly calling the provider's
 * OpenAI-compatible /models endpoint with the user's API Key and Base URL.
 * Falls back to a simple /chat/completions ping with the first model ID
 * if /models is not supported.
 */
export async function testProviderConnection(
  apiKey: string,
  baseUrl: string,
  modelIds: string[],
): Promise<{ ok: boolean; foundModels: string[]; error?: string }> {
  const base = baseUrl.replace(/\/+$/, "");
  if (!base || !apiKey.trim()) {
    return { ok: false, foundModels: [], error: "请先填写 API Key 和 Base URL。" };
  }

  // Try listing models via GET /models (OpenAI-compatible)
  try {
    const res = await fetch(`${base}/models`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${apiKey.trim()}`,
      },
    });
    if (res.ok) {
      const body = await res.json();
      const remoteModels: string[] = (body?.data ?? []).map((m: { id?: string }) => m.id).filter(Boolean);
      if (remoteModels.length > 0) {
        // Check which of our configured model IDs exist on the remote
        const matched = modelIds.filter((id) => remoteModels.includes(id));
        const unmatched = modelIds.filter((id) => !remoteModels.includes(id));
        if (matched.length > 0) {
          const msg = unmatched.length > 0
            ? `（注意：${unmatched.join(", ")} 未在服务商模型列表中找到）`
            : "";
          return { ok: true, foundModels: matched, error: msg || undefined };
        }
        return {
          ok: false,
          foundModels: [],
          error: `连接成功，但配置的模型 ID 均不在服务商列表中。可用模型：${remoteModels.slice(0, 10).join(", ")}${remoteModels.length > 10 ? " ..." : ""}`,
        };
      }
      // /models returned OK but empty data — still a successful connection
      return { ok: true, foundModels: [], error: "连接成功，但服务商未返回模型列表。" };
    }
    // /models not supported (404, 403, etc.) — fall through to ping test
  } catch {
    // Network error on /models — fall through to ping test
  }

  // Fallback: send a tiny chat completion request to verify auth
  if (modelIds.length === 0) {
    return { ok: false, foundModels: [], error: "请至少配置一个模型 ID。" };
  }
  try {
    const res = await fetch(`${base}/chat/completions`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${apiKey.trim()}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        model: modelIds[0],
        messages: [{ role: "user", content: "hi" }],
        max_tokens: 1,
      }),
    });
    if (res.ok) {
      return { ok: true, foundModels: [modelIds[0]] };
    }
    const errBody = await res.text().catch(() => "");
    if (res.status === 401 || res.status === 403) {
      return { ok: false, foundModels: [], error: "认证失败，请检查 API Key 是否正确。" };
    }
    return { ok: false, foundModels: [], error: `请求失败 (${res.status}): ${errBody.slice(0, 200)}` };
  } catch (err) {
    return { ok: false, foundModels: [], error: `网络错误: ${String(err)}` };
  }
}
